import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import useFiltroLembrado from '../hooks/useFiltroLembrado';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Caixa Postal do e-CAC (Integra Contador). Espelho das mensagens da caixa postal
// RFB de todas as empresas ativas, lidas com o certificado do escritório (exige procuração
// eletrônica). Sincronização em blocos (a tela chama de novo até concluir); o corpo da
// mensagem é buscado uma única vez e fica guardado.

const API = process.env.REACT_APP_API_BASE;
const URL = `${API}/api/fiscal/caixa-postal`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const dataDe = (txt) => {
  // 'dd/mm/aaaa hh:mm' -> Date (para o corte de 30 dias); inválida vira null
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(txt || '');
  return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
};

function Detalhe({ texto }) {
  // O corpo pode vir como JSON (campos) ou como texto/HTML do e-CAC.
  let corpo = texto;
  try {
    const j = JSON.parse(texto);
    if (j && typeof j === 'object') {
      const partes = [];
      const achatar = (obj, prefixo = '') => Object.entries(obj).forEach(([k, v]) => {
        if (v && typeof v === 'object') achatar(v, `${prefixo}${k}.`);
        else partes.push([prefixo + k, String(v ?? '')]);
      });
      achatar(j);
      const html = partes.find(([k]) => /corpo|conteudo|mensagem|texto|html/i.test(k) && /<\w+/.test(partes.find(([kk]) => kk === k)?.[1] || ''));
      if (html) corpo = html[1];
      else {
        return (
          <table className="table table-sm mb-0"><tbody>
            {partes.map(([k, v]) => <tr key={k}><td className="fz-sub" style={{ whiteSpace: 'nowrap' }}>{k}</td><td style={{ overflowWrap: 'anywhere' }}>{v}</td></tr>)}
          </tbody></table>
        );
      }
    }
  } catch { /* não é JSON: segue como texto/HTML */ }
  if (/<\w+[^>]*>/.test(corpo || '')) {
    // HTML do e-CAC renderizado isolado (iframe sandbox, sem scripts)
    return <iframe title="Mensagem" sandbox="" srcDoc={corpo} style={{ width: '100%', minHeight: '60vh', border: '1px solid #e3e7ee', borderRadius: 8, background: '#fff' }} />;
  }
  return <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{corpo || '—'}</pre>;
}

export default function FiscalCaixaPostal() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  // filtros lembrados: trocar de tela e voltar mantém a seleção
  const [busca, setBusca] = useFiltroLembrado('fiscalCaixa.busca', '');
  const [empresa, setEmpresa] = useFiltroLembrado('fiscalCaixa.empresa', '');
  const [soNaoLidas, setSoNaoLidas] = useFiltroLembrado('fiscalCaixa.naoLidas', false);
  const [sync, setSync] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [leitura, setLeitura] = useState(null);       // { item, detalhe | null }
  const montado = useRef(true);
  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${URL}/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar a caixa postal'))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  // Sincronização em blocos: chama de novo até o backend dizer "concluido".
  const sincronizar = async (cod) => {
    setSincronizando(true); setErro(''); setMsg('');
    let reiniciar = true;
    try {
      for (;;) {
        const corpo = cod ? { empresa: cod } : { reiniciar };
        const r = await axios.post(`${URL}/sincronizar/`, corpo, { headers: headers() });
        reiniciar = false;
        if (!montado.current) return;
        setSync(r.data.sync);
        if (r.data.sync.concluido) {
          const s = r.data.sync;
          setMsg(cod
            ? `Empresa sincronizada: ${s.mensagens} mensagem(ns) na caixa postal.`
            : `Sincronizado: ${s.mensagens} mensagem(ns) de ${s.total} empresa(s)${s.erros?.length ? ` · ${s.erros.length} empresa(s) com erro` : ''}`);
          break;
        }
      }
      carregar();
    } catch (e) {
      if (montado.current) setErro(e.response?.data?.mensagem || 'Não foi possível sincronizar a caixa postal');
    } finally {
      if (montado.current) setSincronizando(false);
    }
  };

  const abrir = (item) => {
    setLeitura({ item, detalhe: null });
    axios.get(`${URL}/${item.id}/detalhe/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setLeitura((l) => (l && l.item.id === item.id ? { item, detalhe: r.data.detalhe, info: r.data.info } : l)))
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível carregar a mensagem'); setLeitura(null); });
  };

  const itens = useMemo(() => dados?.itens || [], [dados]);
  const lista = useMemo(() => {
    const termo = semAcento(busca.trim());
    return itens.filter((i) => {
      if (empresa && String(i.cod) !== String(empresa)) return false;
      if (soNaoLidas && i.lida) return false;
      return !termo || semAcento(i.assunto).includes(termo) || semAcento(i.cliente).includes(termo) || String(i.cod) === busca.trim();
    });
  }, [itens, busca, empresa, soNaoLidas]);

  const kpis = useMemo(() => {
    const corte = new Date(); corte.setDate(corte.getDate() - 30);
    const naoLidas = itens.filter((i) => !i.lida);
    return [
      { rotulo: 'Não lidas', valor: naoLidas.length, icone: 'fa-envelope', cor: 'c-bad' },
      { rotulo: 'Relevantes não lidas', valor: naoLidas.filter((i) => i.relevante).length, icone: 'fa-star', cor: 'c-hot' },
      { rotulo: 'Empresas com não lida', valor: new Set(naoLidas.map((i) => i.cod)).size, icone: 'fa-building', cor: 'c-warn' },
      { rotulo: 'Últimos 30 dias', valor: itens.filter((i) => { const d = dataDe(i.data_envio); return d && d >= corte; }).length, icone: 'fa-calendar', cor: 'c-navy' },
      { rotulo: 'No espelho', valor: itens.length, icone: 'fa-inbox', cor: 'c-ok' },
    ];
  }, [itens]);

  const pct = sync && sync.total ? Math.round((sync.indice / sync.total) * 100) : 0;
  const ultimo = sync || dados?.sync;

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-inbox header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Caixa Postal (e-CAC)</h2>
          </div>
          <div className="gr-toolbar">
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando || sincronizando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
            {empresa && (
              <button type="button" className="gr-btn" onClick={() => sincronizar(Number(empresa))} disabled={sincronizando} title="Busca as mensagens só da empresa filtrada">
                <i className="fas fa-building" aria-hidden="true"></i>Sincronizar esta empresa
              </button>
            )}
            <button type="button" className="gr-btn primaria" onClick={() => sincronizar()} disabled={sincronizando}>
              <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-down'}`} aria-hidden="true"></i>
              {sincronizando ? `Sincronizando ${pct}%` : 'Sincronizar todas'}
            </button>
          </div>
        </div>

        {sincronizando && sync && sync.total != null && (
          <div className="mb-3">
            <div className="progress" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${pct}%`, background: '#00b5cc' }}></div></div>
            <div className="fz-sub mt-1">{sync.indice} de {sync.total} empresas lidas · {sync.mensagens} mensagem(ns) · só leitura no Integra Contador</div>
          </div>
        )}
        {!sincronizando && (
          <div className="fz-sub mb-2">
            <i className="fas fa-circle-info me-1"></i>
            {ultimo?.concluido_em
              ? <>Última sincronização: {ultimo.concluido_em}{ultimo.erros?.length ? ` · ${ultimo.erros.length} empresa(s) com erro` : ''}.</>
              : ultimo && !ultimo.concluido
                ? <>Sincronização incompleta ({ultimo.indice}/{ultimo.total}). Clique em "Sincronizar todas" para concluir.</>
                : <>Ainda não sincronizado — clique em "Sincronizar todas".</>}
            {' '}Mensagens da caixa postal do e-CAC lidas pelo Integra Contador; empresa sem procuração eletrônica aparece nos erros.
          </div>
        )}
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}
        {!sincronizando && ultimo?.erros?.length > 0 && (
          <details className="small mb-2"><summary className="text-danger">Empresas com erro na sincronização</summary>
            <ul className="mb-0">{ultimo.erros.map((e) => <li key={e}>{e}</li>)}</ul>
          </details>
        )}

        <div className="po-kpis" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
          {kpis.map((k) => (
            <div key={k.rotulo} className={`po-kpi ${k.cor} ${k.valor ? '' : 'po-zero'}`}>
              <div className="po-kpi-rotulo"><i className={`fas ${k.icone}`} aria-hidden="true"></i>{k.rotulo}</div>
              <div className="po-kpi-valor">{k.valor}</div>
            </div>
          ))}
        </div>

        <div className="fz-filtros">
          <input className="po-busca" placeholder="Buscar por assunto, empresa ou código" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="fz-sel" style={{ maxWidth: 340 }} value={empresa} onChange={(e) => setEmpresa(e.target.value)} aria-label="Empresa">
            <option value="">Todas as empresas</option>
            {(dados?.empresas || []).map((e) => <option key={e.cod} value={e.cod}>{e.cod} · {e.cliente}</option>)}
          </select>
          <label className="po-toggle"><input type="checkbox" checked={soNaoLidas} onChange={(e) => setSoNaoLidas(e.target.checked)} />Só não lidas</label>
        </div>

        <section className="po-card po-enter fz-topo-fixo">
          <header className="po-card-head">
            <h3><i className="fas fa-envelope" aria-hidden="true"></i>Mensagens</h3>
            <span className="badge bg-light text-dark">{lista.length}</span>
          </header>
          <div className="po-card-body">
            {/* sem rolagem interna: a lista rola com a página e o cabeçalho fica fixo no topo */}
            <div style={{ minHeight: 380 }}>
              <table className="table table-hover po-table align-middle mb-0">
                <thead><tr><th>Empresa</th><th>Assunto</th><th>Enviada em</th><th>Situação</th><th></th></tr></thead>
                <tbody>
                  {lista.map((i) => (
                    <tr key={i.id} style={i.lida ? undefined : { background: '#f3fbfd' }}>
                      <td><div className="fw-semibold">{i.cliente}</div><div className="fz-sub">{i.cod}</div></td>
                      <td>
                        <div className={i.lida ? '' : 'fw-semibold'} style={{ overflowWrap: 'anywhere' }}>
                          {i.relevante && <i className="fas fa-star me-1" style={{ color: '#f1be48' }} title="Relevante"></i>}
                          {i.assunto || '(sem assunto)'}
                        </div>
                        {i.remetente && <div className="fz-sub">{i.remetente}</div>}
                      </td>
                      <td className="small" style={{ whiteSpace: 'nowrap' }}>{i.data_envio || '—'}</td>
                      <td>
                        {i.lida
                          ? <span className="fz-selo ok" title={i.data_leitura ? `lida em ${i.data_leitura}` : undefined}><i className="fas fa-envelope-open"></i>lida</span>
                          : <span className="fz-selo alerta"><i className="fas fa-envelope"></i>não lida</span>}
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => abrir(i)}><i className="fas fa-eye me-1"></i>Ler</button>
                      </td>
                    </tr>
                  ))}
                  {lista.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">{itens.length ? 'Nenhuma mensagem com esses filtros.' : 'Nenhuma mensagem no espelho — sincronize para buscar.'}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {leitura && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-envelope-open-text me-2" style={{ color: '#00b5cc' }}></i>{leitura.item.assunto || 'Mensagem'}</h5>
                    <small style={{ color: '#6c757d' }}>
                      {leitura.item.cod} · {leitura.item.cliente}{leitura.item.data_envio ? ` · enviada em ${leitura.item.data_envio}` : ''}
                      {leitura.info?.data_ciencia ? ` · ciência em ${leitura.info.data_ciencia}` : ''}
                      {leitura.info?.numero_controle ? ` · controle ${leitura.info.numero_controle}` : ''}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setLeitura(null)}></button>
                </div>
                <div className="modal-body">
                  {leitura.detalhe === null
                    ? <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-2x mb-3" style={{ color: '#00b5cc' }}></i><div>Buscando a mensagem...</div></div>
                    : <Detalhe texto={leitura.detalhe} />}
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary" onClick={() => setLeitura(null)}><i className="fas fa-times me-2"></i>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
